import { useEffect, useState } from "react";
import { Alert } from "react-bootstrap";
import { useAuthorization } from "../Auth/AuthorizationProvider";
import {
    INSTALL_HINT_DISMISSED_KEY,
    isRunningStandalone,
    parseDismissedAt,
    shouldShowIosInstallHint,
} from "../Utils/pwa";

/**
 * iOS Safari has no `beforeinstallprompt` event and no browser affordance that
 * advertises installation, so users must be told how to add the portal to their
 * home screen. Chromium browsers surface their own install affordance and are
 * intentionally not handled here.
 *
 * Withheld until the user is authorized so the hint does not appear over the
 * sign-in page.
 */
export function InstallAppHint() {
    const { isAuthorized } = useAuthorization();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        if (!isAuthorized) {
            setVisible(false);
            return;
        }

        let dismissedAt: number | null = null;
        try {
            dismissedAt = parseDismissedAt(localStorage.getItem(INSTALL_HINT_DISMISSED_KEY));
        } catch (error) {
            // Private browsing or blocked storage: fall back to showing the hint.
            console.warn("Unable to read install hint dismissal state:", error);
        }

        setVisible(
            shouldShowIosInstallHint({
                userAgent: navigator.userAgent,
                maxTouchPoints: navigator.maxTouchPoints,
                isStandalone: isRunningStandalone(),
                dismissedAt,
                now: Date.now(),
            })
        );
    }, [isAuthorized]);

    if (!visible) return null;

    const dismiss = () => {
        setVisible(false);
        try {
            localStorage.setItem(INSTALL_HINT_DISMISSED_KEY, String(Date.now()));
        } catch (error) {
            // Dismissal simply will not persist across sessions.
            console.warn("Unable to persist install hint dismissal:", error);
        }
    };

    return (
        <Alert
            variant="info"
            dismissible
            onClose={dismiss}
            data-testid="install-app-hint"
            style={{
                position: "fixed",
                left: "0.75rem",
                right: "0.75rem",
                bottom: "calc(80px + env(safe-area-inset-bottom, 0px))",
                zIndex: 1040,
                marginBottom: 0,
                fontSize: "0.9rem",
            }}
        >
            <strong>Install the portal</strong>
            <div>
                Tap the Share button, then choose <strong>Add to Home Screen</strong> to
                use the Marketplace Content Portal as an app.
            </div>
        </Alert>
    );
}

export default InstallAppHint;
